/**
 * My Tax — Employee self-service tax view.
 *
 * Surfaces YTD PAYE paid, gross emoluments and effective tax rate (from the
 * staff statutory-position endpoint), the reliefs applied (Rent Relief
 * declared? / NHF status), a link to payslip history, and a one-click
 * "Download Tax Certificate" (year selector) hitting the new
 * /payroll/me/tax-certificate PDF endpoint.
 *
 * Permission: self (payroll.read) — the server enforces is_self for users
 * who hold only payroll.read, and the certificate endpoint derives the
 * employee from the auth token (no employee_id param).
 */

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
  Button,
  Divider,
  Chip,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ReceiptLong as TaxIcon,
  Download as DownloadIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { TYPE } from '@/theme/typography';
import { myTaxApi } from '@/api/endpoints/hr';
import { downloadBlob } from '@/utils/format';

interface Props {
  /** Employee id (from portal profile) — needed for the staff YTD endpoint. */
  employeeId: string;
  /** Current annual_rent_paid from employee record (null = not declared). */
  annualRentPaid: string | null;
  /** Current NHF opt-in status. */
  nhfOptIn: boolean;
  /** Currency symbol for display. */
  currencySymbol?: string;
}

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = [CURRENT_YEAR, CURRENT_YEAR - 1, CURRENT_YEAR - 2];

function fmtMoney(value: string | number, symbol: string): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (!Number.isFinite(n)) return `${symbol}0`;
  return `${symbol}${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}

export default function MyTaxCard({
  employeeId,
  annualRentPaid,
  nhfOptIn,
  currencySymbol = '₦',
}: Props) {
  const navigate = useNavigate();
  const [year, setYear] = useState<number>(CURRENT_YEAR);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['my-tax-ytd', employeeId, year],
    queryFn: () => myTaxApi.getStaffYtd(employeeId, year).then((r) => r.data),
    enabled: !!employeeId,
    retry: false,
  });

  const totals = data?.totals;
  const gross = Number(totals?.gross_pay ?? 0);
  const paye = Number(totals?.paye ?? 0);
  const effRate = data?.effective_tax_rate_pct ?? '0';
  const hasData = gross > 0 || paye > 0;

  const rentDeclared = annualRentPaid !== null && annualRentPaid !== '';

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError(null);
    try {
      const res = await myTaxApi.downloadTaxCertificate(year);
      downloadBlob(res.data as Blob, `tax_certificate_${year}.pdf`);
    } catch {
      setDownloadError('Could not generate your tax certificate. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <TaxIcon fontSize="small" color="primary" />
            <Typography variant="subtitle1" fontWeight={TYPE.semibold}>
              My Tax
            </Typography>
          </Stack>
          <Select
            size="small"
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            sx={{ minWidth: 96, fontSize: TYPE.sm }}
          >
            {YEAR_OPTIONS.map((y) => (
              <MenuItem key={y} value={y}>
                {y}
              </MenuItem>
            ))}
          </Select>
        </Stack>

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : !hasData || isError ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
            No tax data yet for {year}. Once your first payroll run for the year is approved,
            your year-to-date PAYE and reliefs will show here.
          </Typography>
        ) : (
          <>
            {/* YTD figures */}
            <Stack direction="row" spacing={2} sx={{ mb: 1.5 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  PAYE Paid (YTD)
                </Typography>
                <Typography variant="h6" fontWeight={TYPE.semibold}>
                  {fmtMoney(paye, currencySymbol)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Gross (YTD)
                </Typography>
                <Typography variant="h6" fontWeight={TYPE.semibold}>
                  {fmtMoney(gross, currencySymbol)}
                </Typography>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Effective Rate
                </Typography>
                <Typography variant="h6" fontWeight={TYPE.semibold}>
                  {effRate}%
                </Typography>
              </Box>
            </Stack>

            <Divider sx={{ my: 1.5 }} />

            {/* Reliefs applied */}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Reliefs applied
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
              <Chip
                size="small"
                label={
                  rentDeclared
                    ? `Rent relief: ${fmtMoney(annualRentPaid as string, currencySymbol)}/yr`
                    : 'Rent relief: not declared'
                }
                color={rentDeclared ? 'success' : 'default'}
                variant={rentDeclared ? 'filled' : 'outlined'}
              />
              <Chip
                size="small"
                label={nhfOptIn ? 'NHF: opted in (2.5%)' : 'NHF: opted out'}
                color={nhfOptIn ? 'primary' : 'default'}
                variant={nhfOptIn ? 'filled' : 'outlined'}
              />
            </Stack>
          </>
        )}

        {downloadError && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {downloadError}
          </Alert>
        )}

        {/* Actions */}
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 1 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadIcon />}
            onClick={handleDownload}
            disabled={downloading}
          >
            {downloading ? 'Preparing...' : 'Download Tax Certificate'}
          </Button>
          <Button
            size="small"
            endIcon={<ArrowIcon fontSize="small" />}
            onClick={() => navigate('/profile', { state: { tab: 3 } })}
            sx={{ fontSize: TYPE.sm }}
          >
            Payslip history
          </Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
